import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Car, List, X, DownloadSimple, UserCircle } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [showIOSGuide, setShowIOSGuide] = React.useState(false);
  const [isInstalled, setIsInstalled] = React.useState(false);
  const location = useLocation();

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  React.useEffect(() => {
    // Check if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Pick up prompt captured before React loaded
    if (window.__pwaPrompt) {
      setDeferredPrompt(window.__pwaPrompt);
    }

    const handler = (e) => {
      e.preventDefault();
      window.__pwaPrompt = e;
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') setIsInstalled(true);
      setDeferredPrompt(null);
    } else {
      // iOS or desktop without prompt — show guide
      setShowIOSGuide(true);
    }
  };

  const navLinks = [
    { href: '/', label: 'Accueil' },
    { href: '/analyse', label: 'Analyser' },
  ];

  return (
    <>
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="https://www.ecocasse.fr" className="flex items-center gap-2 group" data-testid="header-logo-link">
              <div className="w-9 h-9 md:w-10 md:h-10 bg-[#E63946] rounded-lg flex items-center justify-center shadow-sm shrink-0">
                <Car size={22} weight="fill" className="text-white" />
              </div>
              <span
                className="font-['Outfit'] text-base md:text-lg font-extrabold tracking-tight leading-none text-gray-900 whitespace-nowrap"
                data-testid="header-logo-text"
              >
                Trouver<span className="text-[#E63946]">Ma</span>Piece
              </span>
            </a>

            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm uppercase tracking-[0.15em] font-semibold transition-colors ${
                    location.pathname === link.href
                      ? 'text-[#E63946]'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {!isInstalled && (
                <button
                  onClick={handleInstall}
                  className="flex items-center gap-2 border-2 border-[#E63946] text-[#E63946] px-4 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-[#E63946] hover:text-white transition-all"
                  data-testid="header-install-btn"
                >
                  <DownloadSimple size={16} weight="bold" />
                  Installer l'app
                </button>
              )}

              <Link
                to="/pro/login"
                className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] font-semibold text-gray-600 hover:text-[#1B4332] transition-colors"
                data-testid="header-pro-login-btn"
              >
                <UserCircle size={20} weight="bold" />
                Espace Pro
              </Link>

              <Link
                to="/analyse"
                className="bg-[#E63946] text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-[#D90429] transition-colors"
                data-testid="header-analyze-btn"
              >
                Analyser mon vehicule
              </Link>
            </nav>

            <div className="flex items-center gap-3 md:hidden">
              {!isInstalled && (
                <button
                  onClick={handleInstall}
                  className="flex items-center gap-1.5 border-2 border-[#E63946] text-[#E63946] px-3 py-2 text-xs font-bold uppercase tracking-wide"
                  data-testid="header-install-btn-mobile"
                >
                  <DownloadSimple size={14} weight="bold" />
                  Installer
                </button>
              )}
              <button
                className="text-gray-900 p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.nav
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4"
              >
                <div className="flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-sm uppercase tracking-[0.15em] font-semibold ${
                        location.pathname === link.href
                          ? 'text-[#E63946]'
                          : 'text-gray-600'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/pro/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm uppercase tracking-[0.15em] font-semibold text-gray-600 flex items-center gap-2"
                    data-testid="mobile-pro-login-btn"
                  >
                    <UserCircle size={18} weight="bold" />
                    Espace Pro
                  </Link>
                  <Link
                    to="/analyse"
                    onClick={() => setMobileMenuOpen(false)}
                    className="bg-[#E63946] text-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-center"
                  >
                    Analyser mon vehicule
                  </Link>
                </div>
              </motion.nav>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* iOS/Desktop install guide modal */}
      <AnimatePresence>
        {showIOSGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowIOSGuide(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-5">
                <h3 className="font-['Outfit'] text-lg font-bold text-gray-900">
                  {isIOS ? 'Installer sur iPhone' : 'Installer l\'application'}
                </h3>
                <button onClick={() => setShowIOSGuide(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>

              {isIOS ? (
                <div className="space-y-4 text-sm text-gray-600">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 bg-[#E63946] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    <p>Appuyez sur le bouton <strong>Partager</strong> <span className="inline-block text-lg leading-none align-middle">&#x2BAD;</span> en bas de Safari</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 bg-[#E63946] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    <p>Faites defiler et appuyez sur <strong>"Sur l'ecran d'accueil"</strong></p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 bg-[#E63946] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                    <p>Appuyez sur <strong>"Ajouter"</strong></p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 text-sm text-gray-600">
                  <p>Pour installer TrouverMaPiece sur votre appareil :</p>
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 bg-[#E63946] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    <p>Ouvrez ce site dans <strong>Google Chrome</strong></p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 bg-[#E63946] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    <p>Appuyez sur le menu <strong>&#x22EE;</strong> (3 points) en haut a droite</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 bg-[#E63946] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                    <p>Selectionnez <strong>"Installer l'application"</strong> ou <strong>"Ajouter a l'ecran d'accueil"</strong></p>
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowIOSGuide(false)}
                className="w-full mt-6 bg-[#E63946] text-white py-3 rounded-lg font-semibold text-sm hover:bg-[#D90429] transition-colors"
              >
                J'ai compris
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
